/**
 * LineItemsSection - Line items validation section
 */

import { FileText } from 'lucide-react';
import { useValidationStore } from '../../store';
import { ValidationLineItemRow } from './ValidationLineItemRow';
import { ValidationSection } from './ValidationSection';

export function LineItemsSection({
  extractedData,
}: {
  extractedData: {
    lineItems?: Array<{
      section: string;
      lineNumber: number;
      lineNumberString: string;
      description: string;
      amount: number;
      borrowerPaidAtClosing?: number;
      borrowerPaidBeforeClosing?: number;
      sellerPaidAtClosing?: number;
      sellerPaidBeforeClosing?: number;
      paidByOthers?: number;
    }>;
    totalLoanCosts?: number;
    totalClosingCosts?: number;
    borrowerPaidAtClosing?: number;
    borrowerPaidBeforeClosing?: number;
    sellerPaidAtClosing?: number;
    sellerPaidBeforeClosing?: number;
  };
}) {
  const { expandedSections, toggleSection } = useValidationStore();
  const isExpanded = expandedSections.has('lineItems');
  const isSummaryExpanded = expandedSections.has('lineItemsSummary');

  if (!extractedData.lineItems || extractedData.lineItems.length === 0) {
    return null;
  }

  // Group line items by section
  const groupedItems = extractedData.lineItems.reduce(
    (acc, item) => {
      if (!acc[item.section]) {
        acc[item.section] = [];
      }
      acc[item.section]!.push(item);
      return acc;
    },
    {} as Record<string, typeof extractedData.lineItems>,
  );

  const hasSummary =
    extractedData.totalLoanCosts ||
    extractedData.totalClosingCosts ||
    extractedData.borrowerPaidAtClosing ||
    extractedData.borrowerPaidBeforeClosing ||
    extractedData.sellerPaidAtClosing ||
    extractedData.sellerPaidBeforeClosing;

  return (
    <>
      {/* Line Items Summary Section */}
      {hasSummary && (
        <ValidationSection
          title="Line Items Summary"
          description="Total costs and payment breakdown"
          icon={<FileText className="h-5 w-5 text-muted-foreground" />}
          isExpanded={isSummaryExpanded}
          onToggle={() => toggleSection('lineItemsSummary')}
        >
          <div className="space-y-3">
            {extractedData.totalLoanCosts && (
              <div className="flex justify-between items-center p-3 bg-muted/30 rounded-lg">
                <span className="text-sm font-medium">Total Loan Costs</span>
                <span className="text-sm font-bold">
                  $
                  {extractedData.totalLoanCosts.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </div>
            )}
            {extractedData.totalClosingCosts && (
              <div className="flex justify-between items-center p-3 bg-muted/30 rounded-lg">
                <span className="text-sm font-medium">Total Closing Costs</span>
                <span className="text-sm font-bold">
                  $
                  {extractedData.totalClosingCosts.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </div>
            )}
            {extractedData.borrowerPaidAtClosing !== undefined && (
              <div className="flex justify-between items-center p-3 bg-muted/30 rounded-lg">
                <span className="text-sm font-medium">Borrower Paid at Closing</span>
                <span className="text-sm font-bold">
                  $
                  {extractedData.borrowerPaidAtClosing.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </div>
            )}
            {extractedData.borrowerPaidBeforeClosing !== undefined && (
              <div className="flex justify-between items-center p-3 bg-muted/30 rounded-lg">
                <span className="text-sm font-medium">Borrower Paid Before Closing</span>
                <span className="text-sm font-bold">
                  $
                  {extractedData.borrowerPaidBeforeClosing.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </div>
            )}
            {extractedData.sellerPaidAtClosing !== undefined && (
              <div className="flex justify-between items-center p-3 bg-muted/30 rounded-lg">
                <span className="text-sm font-medium">Seller Paid at Closing</span>
                <span className="text-sm font-bold">
                  $
                  {extractedData.sellerPaidAtClosing.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </div>
            )}
            {extractedData.sellerPaidBeforeClosing !== undefined && (
              <div className="flex justify-between items-center p-3 bg-muted/30 rounded-lg">
                <span className="text-sm font-medium">Seller Paid Before Closing</span>
                <span className="text-sm font-bold">
                  $
                  {extractedData.sellerPaidBeforeClosing.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </div>
            )}
          </div>
        </ValidationSection>
      )}

      {/* Detailed Line Items Section */}
      <ValidationSection
        title="Line Items Detail"
        description="All closing cost line items from the document"
        icon={<FileText className="h-5 w-5 text-muted-foreground" />}
        isExpanded={isExpanded}
        onToggle={() => toggleSection('lineItems')}
      >
        <div className="space-y-6">
          {Object.entries(groupedItems).map(([section, items]) => (
            <div key={section}>
              <h4 className="font-semibold mb-3 text-sm uppercase tracking-wide text-muted-foreground">
                Section {section}
              </h4>
              <div className="space-y-2">
                {items.map((item, idx) => (
                  <ValidationLineItemRow
                    key={`${section}-${item.lineNumber}`}
                    section={section}
                    itemIndex={idx}
                    lineNumber={item.lineNumberString}
                    label={item.description}
                    amount={item.amount}
                    borrowerPaidAtClosing={item.borrowerPaidAtClosing}
                    borrowerPaidBeforeClosing={item.borrowerPaidBeforeClosing}
                    sellerPaidAtClosing={item.sellerPaidAtClosing}
                    sellerPaidBeforeClosing={item.sellerPaidBeforeClosing}
                    paidByOthers={item.paidByOthers}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </ValidationSection>
    </>
  );
}
